import { useEffect, useState, useRef, useCallback } from "react";
import { useGetRoomsQuery, useUpdateChatNameMutation, useDeleteChatRoomMutation } from "./services/user";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil, Search, Trash2, Plus } from "lucide-react";
import "./Sidebar.css";
import { setNewChat } from "./services/GlobalSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useGetUserDataQuery } from "./services/auth";

const Sidebar = ({ isCollapsed = false, sidebarWidth = 240, onSidebarWidthChange }) => {
  const dispatch = useDispatch();
  const { roomId } = useParams();
  const isResizing = useRef(false);
  const [localWidth, setLocalWidth] = useState(sidebarWidth);
  // Use Redux or localStorage so we have token before first fetch (fixes post-login timing)
  const authToken = useSelector((state) => state.global.authToken) || localStorage.getItem("access_token");
  const { data: roomsData, refetch } = useGetRoomsQuery(undefined, { skip: !authToken });
  const [activeChat, setActiveChat] = useState(null);
  const [tempChats, setTempChats] = useState([]);
  const [editingChat, setEditingChat] = useState(null);
  const [chatName, setChatRoomName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const navigate = useNavigate();
  const [updateChatName] = useUpdateChatNameMutation();
  const [deleteChatRoom] = useDeleteChatRoomMutation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { data: userData } = useGetUserDataQuery(undefined, { skip: !authToken });

  useEffect(() => {
    setLocalWidth(sidebarWidth);
  }, [sidebarWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = Math.min(Math.max(e.clientX, 180), 400);
    setLocalWidth(newWidth);
    onSidebarWidthChange?.(newWidth);
  }, [onSidebarWidthChange]);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e) => {
    isResizing.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    e.preventDefault();
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleNewChat = async () => {
    // Clear any existing temp chats and set new chat flag
    setTempChats([]);
    dispatch(setNewChat(true));
    
    // Navigate to home (new chat) to show welcome screen
    navigate("/chat");
  };

  const handleLogoClick = () => {
    // Navigate to home and show welcome screen
    dispatch(setNewChat(true));
    navigate("/chat");
  };

  const handleEdit = async (room_id) => {
    try {
      await updateChatName({ uuid_field: room_id, chat_room_name: chatName }).unwrap();
      setEditingChat(null);
      refetch();
    } catch (error) {
      toast.error("Failed to update chat name");
      console.error("Failed to update chat name:", error);
    }
  };

  const handleDelete = async (room_id) => {
    try {
      await deleteChatRoom(room_id).unwrap();
      toast.success("Chat deleted successfully");
      refetch();
      if (activeChat === room_id) {
        setActiveChat(null);
        navigate("/chat");
      }
    } catch (error) {
      const errorMessage = error?.data?.error || "Failed to delete chat";
      toast.error(errorMessage);
      console.error("Failed to delete chat:", error);
    }
  };

  const confirmDelete = (room_id) => {
    setChatToDelete(room_id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    handleDelete(chatToDelete);
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const startEditing = (chat) => {
    setEditingChat(chat.uuid_field);
    setChatRoomName(chat.chat_room_name);
  };

  const handleBlur = (chatId) => {
    if (chatName.trim() !== "") {
      handleEdit(chatId);
    } else {
      setEditingChat(null);
    }
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) setSearchQuery("");
  };

  const filterChats = (chats) => {
    if (!searchQuery || !chats) return chats;
    return chats.filter((chat) =>
      chat.chat_room_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const activateChat = (chat) => {
    if (activeChat !== chat.uuid_field) {
      dispatch(setNewChat(false));
      setActiveChat(chat.uuid_field);
      navigate(`/${chat.uuid_field || ""}`);
    } else {
      dispatch(setNewChat(false));
    }
  };

  const renderChats = (categoryName, chats) => {
    if (!chats || chats.length === 0) return null;

    const filteredChats = filterChats(chats);

    if (filteredChats.length === 0) return null;

    return (
      <div className="chat-category" key={categoryName}>
        <h4 className="chat-category-title">{categoryName.toUpperCase()}</h4>
        {filteredChats.map((chat) => (
          <div
            key={chat.uuid_field}
            className={`chat-item ${activeChat === chat.uuid_field ? "active-chat" : ""}`}
            title={chat.chat_room_name || "New Chat"}
            role={editingChat === chat.uuid_field ? undefined : "button"}
            tabIndex={editingChat === chat.uuid_field ? -1 : 0}
            aria-current={activeChat === chat.uuid_field ? "page" : undefined}
            onClick={() => activateChat(chat)}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activateChat(chat);
              }
            }}
          >
            {editingChat === chat.uuid_field ? (
              <input
                type="text"
                className="edit-chat-input"
                value={chatName}
                onChange={(e) => setChatRoomName(e.target.value)}
                onBlur={() => handleBlur(chat.uuid_field)}
                onKeyDown={(e) => e.key === "Enter" && handleEdit(chat.uuid_field)}
                autoFocus
              />
            ) : (
              <div className="chat-item-content">
                <span title={chat.chat_room_name || "New Chat"}>
                  {chat.chat_room_name || "New Chat"}
                </span>
                {chat.uuid_field && (
                  <div className="chat-actions">
                    <Pencil
                      className="edit-icon"
                      size="17px"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(chat);
                      }}
                    />
                    <Trash2
                      className="delete-icon"
                      size="17px"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(chat.uuid_field);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (roomId) {
      setTempChats([]);
      setActiveChat(roomId);
    } else {
      setActiveChat(null);
    }
  }, [roomId]);

  return (
    <div
      className={`custom-sidebar ${isCollapsed ? 'collapsed' : ''}`}
      style={{ width: `${localWidth}px` }}
    >
      <div
        className="sidebar-resize-handle"
        onMouseDown={handleMouseDown}
      />
      <div className="custom-sidebar-header">
        <img
          src="/edge-ai-thinking-logo.png"
          className="custom-sidebar-logo"
          alt="StratusEdge"
          onClick={handleLogoClick}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="sidebar-actions">
        <button type="button" className="sidebar-action-btn new-chat-btn" onClick={handleNewChat}>
          <Plus size={16} />
          {!isCollapsed && <span>New chat</span>}
        </button>

        <button type="button" className="sidebar-action-btn search-chats-btn" onClick={toggleSearch}>
          <Search size={16} />
          {!isCollapsed && <span>Search chats</span>}
        </button>
      </div>

      {isSearchOpen && (
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search chats..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      )}
      
      <div className="past-chats">
        {tempChats.length > 0 && renderChats("New", tempChats)}

        {roomsData ? (
          <>
            {renderChats("Today", roomsData.Today)}
            {renderChats("Previous 7 Days", roomsData["Previous 7 days"])}
            {renderChats("Previous 30 Days", roomsData["Previous month"])}
            {Object.keys(roomsData)
              .filter(key => !["Today", "Previous 7 days", "Previous month", "Previous year", "Older chats"].includes(key))
              .map(monthKey => renderChats(monthKey, roomsData[monthKey]))}
            {renderChats("Previous Year", roomsData["Previous year"])}
            {renderChats("Older Chats", roomsData["Older chats"])}
          </>
        ) : (
          <p className="no-chats">No past chats available</p>
        )}
      </div>

      {!isCollapsed && (
        <div className="sidebar-footer">
          {userData && !userData.is_subscription_active && userData.queries_allowed > 0 && (
            <div className="query-usage">
              <span>{userData.queries_remaining ?? (userData.queries_allowed - userData.queries_used)} queries remaining</span>
              <div className="usage-bar">
                <div
                  className="usage-fill"
                  style={{
                    width: `${Math.min(100, ((userData.queries_used || 0) / (userData.queries_allowed || 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
          <div className="sidebar-user">
            <div className="sidebar-user-avatar" aria-hidden="true">
              {`${userData?.first_name?.[0] || ''}${userData?.last_name?.[0] || ''}`.toUpperCase() || 'U'}
            </div>
            <div className="sidebar-user-meta">
              <div className="sidebar-user-name" title={`${userData?.first_name || ''} ${userData?.last_name || ''}`.trim()}>
                {[userData?.first_name, userData?.last_name].filter(Boolean).join(' ') || 'User'}
              </div>
              <div className="sidebar-user-email" title={userData?.email || ''}>
                {userData?.email || ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Delete Chat</h3>
            <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
            <div className="delete-confirm-buttons">
              <button className="confirm-button" onClick={handleConfirmDelete}>
                Delete
              </button>
              <button className="cancel-button" onClick={cancelDelete}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
